import cx from 'clsx';
import { motion } from 'motion/react';
import { useRef } from 'react';
import type { DisclosureProps as AriaDisclosureProps } from 'react-aria-components';
import {
  Button as AriaButton,
  Disclosure as AriaDisclosure,
  DisclosurePanel as AriaDisclosurePanel,
} from 'react-aria-components';
import { IconChevronBottomOutlined } from '@repo/monochrome-icons';
import styles from './accordion.module.css';

type BaseAccordionSingleProps = {
  children: React.ReactNode;
  className?: string;
  variant?: 'default' | 'container';
} & (
  | { title: string; icon?: React.ReactNode; header?: never }
  | { title?: never; icon?: never; header: JSX.Element }
);

export type AccordionSingleProps = BaseAccordionSingleProps & AriaDisclosureProps;

const AnimatedDisclosurePanel = motion.create(AriaDisclosurePanel);

export function AccordionSingle({
  title,
  icon,
  header,
  children,
  className,
  variant = 'default',
  ...props
}: AccordionSingleProps): React.ReactElement {
  const panelRef = useRef<HTMLDivElement>(null);

  return (
    <AriaDisclosure
      className={cx(styles.accordion, styles[`accordion--${variant}`], className)}
      {...props}
    >
      {({ isExpanded }) => (
        <>
          <div className={styles.accordion__header}>
            <AriaButton
              className={styles.accordion__header__clickable}
              data-testid="accordion-trigger"
              preventFocusOnPress
              slot="trigger"
            >
              {title ? (
                <div className={cx('body-prominent', styles.accordion__label)}>
                  {icon ? <span className={styles.accordion__label_icon}>{icon}</span> : null}
                  {title}
                </div>
              ) : null}
              {header ? header : null}
              <IconChevronBottomOutlined aria-hidden="true" className={styles.accordion__arrow} />
            </AriaButton>
          </div>
          <AnimatedDisclosurePanel
            animate={{
              opacity: isExpanded ? 1 : 0,
              height: isExpanded ? 'auto' : 0,
            }}
            className={styles.accordion__content}
            initial={false}
            onAnimationComplete={() => {
              panelRef.current?.style.setProperty(
                'content-visibility',
                isExpanded ? 'visible' : 'hidden'
              );
            }}
            onAnimationStart={() => {
              panelRef.current?.style.setProperty('content-visibility', 'visible');
            }}
            ref={panelRef}
            style={{ display: 'block' }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
          >
            <main className={styles.accordion__content__inner} role="complementary">
              {children}
            </main>
          </AnimatedDisclosurePanel>
        </>
      )}
    </AriaDisclosure>
  );
}
